import React, { useState } from "react";
import { Button } from "./ui/button";
import {
  Menu,
  X,
  Home,
  User,
  BookOpen,
  Phone,
  Building2,
  FileText,
  Calendar,
  Handshake,
  GraduationCap,
} from "lucide-react";
import { Link, NavLink } from "react-router-dom";
import { colors, images, company, externalLinks, announcementBar } from "../config/site";
import AnnouncementBar from "./AnnouncementBar";

const NAV_ITEMS = [
  { to: "/", label: "Home", icon: Home },
  { to: "/about", label: "About", icon: User },
  { to: "/development", label: "Development", icon: Building2 },
  { to: "/programs", label: "Housing Stability Programs", icon: GraduationCap },
  { to: "/partnerships", label: "Community Partnerships", icon: Handshake },
  { to: "/resources", label: "Resources", icon: FileText },
];

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const closeMenu = () => setIsOpen(false);

  return (
    <>
      <AnnouncementBar />
      <header
        data-testid="site-header"
        className="fixed left-0 right-0 z-50 backdrop-blur-md shadow-sm border-b print:hidden"
        style={{
          top: announcementBar.enabled ? "var(--announcement-h, 44px)" : 0,
          backgroundColor: "rgba(255,255,255,0.96)",
          borderColor: "#E6DFD2",
        }}
      >
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center space-x-3 shrink-0" data-testid="logo-link">
            <img
              src={images.logo}
              alt={`${company.name} Logo`}
              className="h-12 w-auto rounded-md"
            />
            <div className="leading-tight">
              <h1 className="text-lg lg:text-xl font-bold" style={{ color: colors.charcoal }}>
                {company.name}
              </h1>
              <p
                className="text-[11px] lg:text-xs font-medium uppercase tracking-wider"
                style={{ color: colors.sage }}
              >
                {company.tagline}
              </p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
            {NAV_ITEMS.map(({ to, label }) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/"}
                data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                    isActive ? "" : "hover:opacity-80"
                  }`
                }
                style={({ isActive }) => ({
                  color: isActive ? colors.terracotta : colors.charcoal,
                  borderBottom: isActive ? `2px solid ${colors.terracotta}` : "2px solid transparent",
                  paddingBottom: "2px",
                })}
              >
                {label}
              </NavLink>
            ))}
            <Link to="/contact" data-testid="nav-contact-cta">
              <Button
                className="text-white px-5 py-2 rounded-full font-medium text-sm shadow-md hover:shadow-lg transition-all"
                style={{ backgroundColor: colors.primary }}
              >
                Contact
              </Button>
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 rounded-lg hover:bg-stone-100 transition-colors"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            {isOpen ? (
              <X className="w-6 h-6" style={{ color: colors.charcoal }} />
            ) : (
              <Menu className="w-6 h-6" style={{ color: colors.charcoal }} />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div
            className="lg:hidden mt-3 py-4 border-t rounded-lg shadow-lg"
            style={{ borderColor: "#E6DFD2", backgroundColor: colors.white }}
            data-testid="mobile-nav"
          >
            <div className="flex flex-col space-y-1 px-2">
              {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={to === "/"}
                  onClick={closeMenu}
                  className="flex items-center space-x-3 py-3 px-3 rounded-md transition-colors"
                  style={({ isActive }) => ({
                    color: isActive ? colors.terracotta : colors.charcoal,
                    backgroundColor: isActive ? "#F8F4ED" : "transparent",
                  })}
                  data-testid={`mobile-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{label}</span>
                </NavLink>
              ))}
              <Link
                to="/contact"
                onClick={closeMenu}
                className="flex items-center space-x-3 py-3 px-3 rounded-md mt-2 text-white font-medium"
                style={{ backgroundColor: colors.primary }}
                data-testid="mobile-nav-contact"
              >
                <Phone className="w-5 h-5" />
                <span>Contact</span>
              </Link>
              <a
                href={externalLinks.calendly}
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMenu}
                className="flex items-center space-x-3 py-3 px-3 rounded-md text-white font-medium"
                style={{ backgroundColor: colors.terracotta }}
                data-testid="mobile-nav-schedule"
              >
                <Calendar className="w-5 h-5" />
                <span>Schedule a Call</span>
              </a>
              <Link
                to="/admin"
                onClick={closeMenu}
                className="flex items-center space-x-3 py-2 px-3 text-xs text-stone-500 mt-2"
              >
                <BookOpen className="w-4 h-4" />
                <span>Admin Dashboard</span>
              </Link>
            </div>
          </div>
        )}
      </div>
      </header>
    </>
  );
};

export default Header;
